// Create + edit form for an email_providers row.
// /settings/email-providers/new is create mode,
// /settings/email-providers/:id is edit mode.

import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { emailProvidersApi, ApiError } from '../lib/api';
import type { EmailProvider } from '@shared/types';

interface FormState {
  name: string;
  domains: string; // newline-separated for the textarea
  inbox_url_desktop: string;
  inbox_url_ios: string;
  inbox_url_android: string;
  cta_label: string;
  instructions_html: string;
  instructions_link: string;
  sort_order: number;
  active: boolean;
}

const EMPTY: FormState = {
  name: '',
  domains: '',
  inbox_url_desktop: '',
  inbox_url_ios: '',
  inbox_url_android: '',
  cta_label: '',
  instructions_html: '',
  instructions_link: '',
  sort_order: 1000,
  active: true,
};

function fromProvider(p: EmailProvider): FormState {
  return {
    name: p.name,
    domains: p.domains.join('\n'),
    inbox_url_desktop: p.inbox_url_desktop ?? '',
    inbox_url_ios: p.inbox_url_ios ?? '',
    inbox_url_android: p.inbox_url_android ?? '',
    cta_label: p.cta_label ?? '',
    instructions_html: p.instructions_html ?? '',
    instructions_link: p.instructions_link ?? '',
    sort_order: p.sort_order,
    active: p.active,
  };
}

function nullify(s: string): string | null {
  return s.trim().length === 0 ? null : s;
}

function parseDomains(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((d) => d.trim().toLowerCase())
    .filter((d) => d.length > 0);
}

export default function EmailProviderEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [provider, setProvider] = useState<EmailProvider | null>(null);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (isCreate) return;
    setLoading(true);
    emailProvidersApi
      .get(id!)
      .then(({ provider }) => {
        setProvider(provider);
        setForm(fromProvider(provider));
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id, isCreate]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    try {
      const domains = parseDomains(form.domains);
      if (domains.length === 0) {
        throw new Error('Add at least one domain (one per line).');
      }
      if (isCreate) {
        const res = await emailProvidersApi.create({
          name: form.name.trim(),
          domains,
          inbox_url_desktop: nullify(form.inbox_url_desktop),
          inbox_url_ios: nullify(form.inbox_url_ios),
          inbox_url_android: nullify(form.inbox_url_android),
          cta_label: nullify(form.cta_label),
          instructions_html: nullify(form.instructions_html),
          instructions_link: nullify(form.instructions_link),
          sort_order: form.sort_order,
          active: form.active,
        });
        navigate(`/settings/email-providers/${res.id}`, { replace: true });
      } else {
        const res = await emailProvidersApi.update(id!, {
          name: form.name.trim(),
          domains,
          inbox_url_desktop: nullify(form.inbox_url_desktop),
          inbox_url_ios: nullify(form.inbox_url_ios),
          inbox_url_android: nullify(form.inbox_url_android),
          cta_label: nullify(form.cta_label),
          instructions_html: nullify(form.instructions_html),
          instructions_link: nullify(form.instructions_link),
          sort_order: form.sort_order,
          active: form.active,
          expected_updated_at: provider!.updated_at,
        });
        setProvider((p) => (p ? { ...p, ...form, domains, updated_at: res.updated_at } : p));
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'conflict') {
        setErr(
          'Someone else edited this provider. Reload the page and re-apply your changes.',
        );
      } else {
        setErr(e instanceof Error ? e.message : 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <button
        onClick={() => navigate('/settings/email-providers')}
        className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={14} /> All email providers
      </button>

      <header className="mb-6">
        <h2 className="font-display text-2xl text-slate-900 break-words">
          {isCreate ? 'New email provider' : form.name || 'Untitled provider'}
        </h2>
      </header>

      {loading ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <form onSubmit={save} className="space-y-4 max-w-3xl">
          {err && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {err}
            </div>
          )}

          <Field label="Name" hint="Display name shown in the confirmation-page CTA">
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              className="input"
              placeholder="Gmail"
            />
          </Field>

          <Field
            label="Domains"
            hint="One per line. Lowercase. Example: gmail.com / googlemail.com"
          >
            <textarea
              rows={4}
              required
              value={form.domains}
              onChange={(e) => set('domains', e.target.value)}
              className="input font-mono text-xs"
              placeholder={'gmail.com\ngooglemail.com'}
            />
          </Field>

          <fieldset className="border border-slate-200 rounded p-4 space-y-3">
            <legend className="px-2 text-xs font-medium text-slate-700">
              Inbox URLs (per device)
            </legend>
            <p className="text-xs text-slate-500">
              The post-signup confirmation page picks the URL matching the
              user's device. App deep links (e.g.{' '}
              <code>googlegmail://</code>, <code>ms-outlook://</code>) work on
              iOS/Android when the app is installed; web URLs work everywhere.
              Leave a field blank to fall back to the desktop URL, or empty
              everything to fall back to <code>mailto:</code>.
            </p>
            <Field label="Desktop URL">
              <input
                type="url"
                value={form.inbox_url_desktop}
                onChange={(e) => set('inbox_url_desktop', e.target.value)}
                className="input"
                placeholder="https://mail.google.com"
              />
            </Field>
            <Field label="iOS URL">
              <input
                type="text"
                value={form.inbox_url_ios}
                onChange={(e) => set('inbox_url_ios', e.target.value)}
                className="input"
                placeholder="googlegmail://"
              />
            </Field>
            <Field label="Android URL">
              <input
                type="text"
                value={form.inbox_url_android}
                onChange={(e) => set('inbox_url_android', e.target.value)}
                className="input"
                placeholder="googlegmail://"
              />
            </Field>
            <Field
              label="CTA label override"
              hint='Defaults to "Open <name>". Override only if you want different text.'
            >
              <input
                type="text"
                value={form.cta_label}
                onChange={(e) => set('cta_label', e.target.value)}
                className="input"
                placeholder="Open Gmail"
              />
            </Field>
          </fieldset>

          <fieldset className="border border-slate-200 rounded p-4 space-y-3">
            <legend className="px-2 text-xs font-medium text-slate-700">
              Welcome email instructions
            </legend>
            <p className="text-xs text-slate-500">
              Raw HTML inserted into the welcome email at the{' '}
              <code>{'{{{provider_instructions_html}}}'}</code> slot for
              recipients on this provider's domains. Leave blank to fall
              through to the fallback provider configured on{' '}
              <Link to="/settings/welcome-email" className="text-blue-700 underline">
                Welcome Email
              </Link>
              .
            </p>
            <Field label="Instructions (HTML)">
              <textarea
                rows={8}
                value={form.instructions_html}
                onChange={(e) => set('instructions_html', e.target.value)}
                className="input font-mono text-xs"
                placeholder='<p>Open this email and click "Add to Contacts"...</p>'
              />
            </Field>
            <Field label="Reference link" hint="Optional public-docs URL">
              <input
                type="url"
                value={form.instructions_link}
                onChange={(e) => set('instructions_link', e.target.value)}
                className="input"
                placeholder="https://support.google.com/mail/answer/6579"
              />
            </Field>
          </fieldset>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Sort order" hint="Lower = earlier in the admin list">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => set('sort_order', Number(e.target.value) || 0)}
                className="input"
              />
            </Field>
            <div className="flex items-end pb-2">
              <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => set('active', e.target.checked)}
                  className="h-4 w-4"
                />
                Active (used for matching)
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-4">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving
                ? 'Saving…'
                : isCreate
                ? 'Create provider'
                : 'Save changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/settings/email-providers')}
              className="btn-ghost"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">
        {label}
        {hint && <span className="ml-2 font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
